export interface ICheckoutUI {
    isLoadingCart: boolean;
    isLoadingEstimated: boolean;
    isSelectingPaymentMethod: boolean;
    isSpecialProcessing: boolean;
    processing: boolean;
    stateRequiredShip: boolean;
    stateRequiredBilling: boolean;
    coord: ICoord[];
    loadingCoord: boolean;
    requireReset: boolean;
    newCheckout: boolean;
}

export interface ICoord {
    img: string;
    url: string;
    cls: string;
    title: string;
    desc: string;
    CoordProductID: number;
}

export interface ICheckoutObject {
    isMyFrames: boolean;
    isAuthenticated: boolean;
    email: IFormInput;
    phone: IFormInput;
    pwd: IFormInput;
    optIn: boolean;
    createAccount: boolean;
    ship: ICheckoutShipping;
    cart: ICart;
    cstep: ICheckoutStep;
    billing: ICheckoutBilling;
    messages: ICheckoutMessage[];
    CheckoutType: ICheckoutType;
    notes_customer: string;
    notes_gift: string;
    code_gift: IFormInput;
    code_promo: IFormInput;
    code_gift_applied: boolean;
    code_promo_applied: boolean;
    code_promo_type: ISpecialDiscountCodeType;
    code_promo_text: string;
    code_promo_shipping_text: string;
    AddressBookAddresses: IAddressBookAddress[];
    payment_type: string;

    force_normal_checkout: boolean;
    missing_shipping_info: boolean;
    missing_shipping_info_warning: boolean;

    ab: number;
    ab_1: number;
    ab_2: number;

    paypal_order: any;
    paypal_order_init: any;
    stripe_express_order: StripeOrder;

    giftMessageId: number;
    giftMessages: IGiftMessage[];

    msg_shipping_special: string;

    is_fast_shipping: boolean;
    is_fast_shipping_broken: boolean;
    received_fast_ship_warning: boolean;
    UseShop: boolean;
    UseShopForce: boolean;

    myframes_isDropShip: boolean;
    myframes_po: IFormInput;
    myframes_email: string;
    myframes_phone: string;
    discount_code: IDiscountCodeExt | null;

    fbp: string;
    fbc: string;
}

export interface StripeOrder {
    token: string;
    email: string;
    tranType: string;
    methodName: string;
    payerEmail: string;
    payerName: string;
    payerPhone: string;
    shippingAddress: any;
    billingAddress: any;
    shippingOption: any;
    tokenRaw: string;
    dataRaw: string;
    displayedCharge: number;
}

export interface ICart {
    pricing: ICartPricing;
    items: ICartLineItem[];
    messages: ICheckoutMessage[];
    hasBackOrder: boolean;
    cartId: number;
}

export interface ICartPricing {
    subtotal: number;
    subtotal_nogifts: number;
    shipping: number;
    production: number;
    tax: number;
    discount: number;
    discount_shipping: number;
    gift: number;
    total: number;
    isEstShipping: boolean;
    isEstTaxes: boolean;
}

export interface ICartLineItem {
    id: number;
    price: number;
    qty: number;
    title1: string;
    title2: string;
    title3: string;
    detailTitle: string;
    details: string[];
    hasBackOrder: boolean;
    messages: ICheckoutMessage[];
    imgThumb: string;
    imgFull: string;
    itemId: string;
    gradYear: number;
    IsDYF: boolean;
    productId: number;
    lineItemDiscount: number;
    special_msg: string;
    address: IAddress[];

    hasEp: boolean;
    epTitle: string;
    ep1: string;
    ep2: string;
    ep3: string;
    ep4: string;
    ep5: string;
    ep6: string;
    ep7: string;
    ep8: string;
    ep9: string;
    ep10: string;
    epMethodId: number;
    epBorderType: number;
    epWidth: string;
    epHeight: string;
    // Second engraved plate size ("" when the line has no second plate), formatted like epWidth.
    ep2Width: string;
    ep2Height: string;
    epContent: string;

    hasCustom: boolean;
    CustomField1_Title: string;
    CustomField1: string;
    CustomField2_Title: string;
    CustomField2: string;
    CustomField3_Title: string;
    CustomField3: string;
    StoreURL: string;
    MouldingID: number;
    PrimaryMatID: number;
    SecondaryMatID: number;
    URL: string;

    gaVariant: string;
    gaCategory: string;
    gaDiscountCode: string;
    gaPosition: number;
    gaList: string;

    storeId: number;
    discountHighlight: string;

    _showQtyInput: boolean;
    _showingDetail: boolean;
    is_fast_shipping: boolean;

    VolumeDiscountPercent: number;
    VolumeDiscountAmt: number;
}

export interface ICheckoutShipping {
    addr: IAddress;
    addressType: ICheckoutAddressType;
    corr: IAddress;
    correctionAvailable: boolean;
    ignoreCorrection: boolean;
    messages: ICheckoutMessage[];
    shipDays: 5;
    shipOptions: IShippingOption[];
    shipMethod: IShippingOption;
    estimatedPostalCode: string;
    geoloc: IIPAPIGeolocation;
    google_autocomplete: IPlacePredictions[];
    google_autocomplete_details: IPlaceDetailAddressComponent[];
    google_autocomplete_placeid: string;
    AddressID: number;
}

export interface IIPAPIGeolocation {
    ip: string;
    city: string;
    region: string;
    region_code: string;
    country: string;
    country_name: string;
    postal: string;
    latitude: number;
    longitude: number;
    timezone: string;
    asn: string;
    org: string;
    stateCode: string;
}

// DF4 IPlacePrediction / IPlaceAddressComponent (services/checkout/i.d.cs) send exactly these names.
export interface IPlacePredictions {
    description: string;
    place_id: string;
}

export interface IPlaceDetailAddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface ICheckoutBilling {
    ccNumber: IFormInput;
    ccExp: IFormInput;
    ccCSC: IFormInput;
    ccType: ICreditCardType;
    ccTypeName: string;
    addrSameAsShip: boolean;
    addr: IAddress;
    messages: ICheckoutMessage[];
    paymentError: string;
    AddressID: number;
    msg_ccNumber: string;
    msg_ccExp: string;
    msg_ccCSC: string;
}

export interface IAddress {
    firstName: IFormInput;
    lastName: IFormInput;
    companyName: IFormInput;
    address1: IFormInput;
    address2: IFormInput;
    address3: IFormInput;
    address4: IFormInput;
    city: IFormInput;
    state: IFormInput;
    country: IFormInput;
    postalCode: IFormInput;
    addrShowMore: boolean;
    addrShowMoreCompany: boolean;
    isBusinessAddress: boolean;
}

export interface IAddressCountry {
    id: number;
    name: string;
    code: string;
    r: number;
    zip: number;
}
export interface IAddressState {
    id: number;
    name: string;
    code: string;
    ddl: string;
    r: number;
    cid: number;
}

export interface IShippingOption {
    desc: string;
    dates: string;
    price: string;
    shipMethodId: number;
    prodMethodId: number;
    key: number;
    price_shipping: number;
    price_production: number;
    isSuperSaver: boolean;
    SuperSaverDiscount: number;
}

export interface ICheckoutMessage {
    msgType: ICheckoutMessageType;
    msg: string;
}

export enum ICheckoutMessageType {
    generic = 0,
    errormsg = 1,
    backorder = 2,
    tax = 3,
    paypal = 4,
    stripe = 5,
    promo = 6,
    gift = 7,
    cart_qty = 8,
    oci = 9,
    login = 10,
    payment = 11,
    shipping = 12,
}

export enum ICheckoutStep {
    cart = 0,
    checkout = 1,
    oci = 2,
    shipping = 3,
    shipping_zip = 4,
    shipping_correction = 5,
    shipping_methods = 6,
    payment = 7,
    paymentAddr = 8,
    success = 9,
}

export enum ICheckoutAddressType {
    home = 0,
    business = 1,
    apo = 2,
}

export enum ICheckoutType {
    normal = 0,
    paypal_express = 1,
    stripe_express = 2,
    stripe_normal = 3,
    no_payment = 4,
}

export enum ISpecialDiscountCodeType {
    normal = 0,
    add = 1,
    subtract = 2,
    freeshipping = 3,
}

export enum ICreditCardType {
    dummy = 0,
    AmericanExpress = 1,
    Discover = 2,
    MasterCard = 3,
    VISA = 4,
    JCB = 5,
    DinersClub = 6,
    EnRoute = 7,
    Other = 8,
}

// paypal express
export interface IPayPalItem {
    name: string;
    unit_amount: IPayPalMoney;
    tax?: IPayPalMoney;
    quantity: string;
    description: string;
    sku: string;
    category: string;
}

export interface IPayPalMoney {
    currency_code: string;
    value: string;
}

export interface IGiftMessage {
    GiftMessageID: number;
    GiftMessage: string;
    Name: string;
}

export interface IFormInput {
    value: string;
    placeholder: string;
    autocomplete: string;
    valid: IValidationObject;
}
export interface IValidationObject {
    alive: boolean;
    msg: string;
    isError: boolean;
    rgx: string;
    rgx_msg: string;
    minLength: number;
    maxLength: number;
}

export interface IFBObject {
    content_category?: string; // category of the page or product.
    content_name?: string; // name of the page/product.
    content_type?: string; // can be product Or product_group
    content_ids?: string[]; // product IDs associated with the event, such as SKUs. Example: ['ABC123', 'XYZ789']. Only use if not content used
    contents?: IFBContentObject[]; // [{'id': 'ABC123', 'quantity': 2}, {'id': 'XYZ789', 'quantity': 2}] Required: id and quantity.
    delivery_category?: string; // 'home_delivery'
    num_items?: number; // number of items when checkout was initiated. Used with the InitiateCheckout event.
    search_string?: string; // string entered by the user for the search. Used with the Search event.
    status?: boolean; // used with the CompleteRegistration event, to show the status of the registration.
    value?: number; // value of a user performing this event to the business.
    currency?: string; // 'USD'
}

export interface IFBContentObject {
    id: string;
    quantity: number;
    value: number;
    currency: string; // 'USD'
}

export interface ga4_item {
    item_id: string; //       ' REQUIRED: the ID Of the item.
    item_name: string; //     ' REQUIRED: the name of the item.
    affiliation?: string; //   ' a product affiliation to designate a supplying company or brick and mortar store location.
    coupon?: string; //        ' the coupon name/code associated with the item.
    currency: string; // = "USD"
    discount?: number; // ' The monetary discount value associated with the item.
    index: number; // ' The index/position of the item in a list.
    item_brand: string;
    item_category: string; // ' ProductTypeName, The category of the item. If used as part of a category hierarchy or taxonomy then this will be the first category.
    item_category2?: string; //
    item_category3?: string; //
    item_category4?: string; //
    item_category5?: string; //
    item_list_id?: string; //      ' StoreID, The ID of the list in which the item was presented to the user.
    item_list_name?: string; //    ' StoreName, The name of the list in which the item was presented to the user.
    item_variant: string; //      ' 	The item variant or unique code or description for additional item details/options.
    location_id: string; // "ChIJUcHLaPbi54kRtbVMrbSH4MU" ' The location associated with the item. It's recommended to use the Google Place ID that corresponds to the associated item. A custom location ID can also be used.
    price: number;
    quantity: number;
    product_id?: number;
}

export interface ga4_event_begin_checkout {
    currency: string; // = "USD"
    value: number;
    coupon?: string;
    items: ga4_item[];
}
export interface ga4_dle_begin_checkout {
    event: string;
    ecommerce: ga4_event_begin_checkout;
    retarget_items: GA4_retarget_item[];
    ecomm_prodid: string[];
    ecomm_pagetype: string;
    ecomm_totalvalue: number;
    ecomm_inst: string;
    value: number;
}

export interface ga4_event_view_cart {
    currency: string; // = "USD"
    value: number;
    items: ga4_item[];
}
export interface ga4_dle_view_cart {
    event: string;
    ecommerce: ga4_event_view_cart;
}

export interface ga4_event_add_shipping_info {
    currency: string; // = "USD"
    value: number;
    coupon?: string;
    shipping_tier: string; // the shipping tier (e.g. Ground, Air, Next-day) selected for delivery of the purchased item.'
    items: ga4_item[];
}
export interface ga4_dle_add_shipping_info {
    event: string;
    ecommerce: ga4_event_add_shipping_info;
    retarget_items: GA4_retarget_item[];
    ecomm_prodid: string[];
    ecomm_pagetype: string;
    ecomm_totalvalue: number;
    ecomm_inst: string;
    value: number;
}

export interface ga4_event_add_payment_info {
    currency: string; // = "USD"
    value: number;
    coupon?: string;
    payment_type: string; // 	The chosen method of payment.
    items: ga4_item[];
}
export interface ga4_dle_add_payment_info {
    event: string;
    ecommerce: ga4_event_add_payment_info;
    retarget_items: GA4_retarget_item[];
    ecomm_prodid: string[];
    ecomm_pagetype: string;
    ecomm_totalvalue: number;
    ecomm_inst: string;
    value: number;
}
export interface ga4_event_remove_from_cart {
    currency: string; // = "USD"
    value: number;
    items: ga4_item[];
}
export interface ga4_dle_remove_from_cart {
    event: string;
    ecommerce: ga4_event_remove_from_cart;
}

export interface IGA4_dle_event_add_to_cart {
    event: string;
    ecommerce: IGA4_event_add_to_cart;
    retarget_items: GA4_retarget_item[];
    ecomm_prodid: string[];
    ecomm_pagetype: string;
    ecomm_totalvalue: number;
    ecomm_inst: string;
    value: number;
}

export interface IGA4_event_add_to_cart {
    currency: string; // = "USD"
    value: number;
    items: ga4_item[];
}

export interface old_gaProduct {
    id: string;
    name: string;
    brand: string;
    category: string;
    variant: string;
    coupon: string;
    position: number;
    list: string; // product List Name for store listing
    itemId: string;
    quantity: number;
}

export interface IAddressBookAddress {
    AddressID: number;
    AddressTypeID: number;
    Nickname: string;
    FirstName: string;
    LastName: string;
    CompanyName: string;
    AddressLine1: string;
    AddressLine2: string;
    City: string;
    State: string;
    Zipcode: string;
    Country: string;
    Phone1: string;
    Phone2: string;
    Fax: string;
    IsBusinessAddress: boolean;
    isBilling: boolean;
    isShipping: boolean;
}

export interface IPromoObj {
    hasPromo: boolean;
    promo: string;
    modal1: string;
    modal2: string;
    redeemClaimed: boolean;
    redeemOff: string;
    redeemCode: string;
    redeemAvailable: boolean;
}

export interface IPortalSettings {
    DisableReviews: boolean;
    DisableRealImages: boolean;
    DisableProdDescLinks: boolean;
    ProdReturnSnippet: string;
    DisableKlaviyo: boolean;
    PortalID: number;
    CustomerServiceEmail: string;
    CustomerServicePhone: string;
    LogoURL: string;
    CDNDomain: string;
    IMGDomain: string;
}

export interface IDiscountCodeExt {
    discountAmt: number;
    discountAmt_Shipping: number;
    discountPercent: number;
    endDate: string;
    freeShippingMethodID: number;
    minPurchaseAmt: number;
    name: string;
    publicDescription: string;
    publicDescriptionShort: string;
    retail: boolean;
    startDate: string;
    switchOrdersToStoreID: number;
    minQty: number;
    autoApply: boolean;
    showStorePage: boolean;
    showProductPage: boolean;
    showLineItem: boolean;
    showDisclaimer: boolean;
    showFreeShipping: boolean;
    isGiftCertCode: boolean;
    overrideStorePage: string;
    overrideProductPage: string;
    overrideLineItem: string;
    overrideFreeShipping: string;
    disclaimer: string;
    unlimited: boolean;
    showCAF_product: boolean;
    showCAF_store: boolean;
    showRedeemCart: boolean;
    showRedeemProd: boolean;
    showRedeemStore: boolean;
    showRedeemStoreProd: boolean;
    store_promo: string;
    store_promo_aria: string;
}

export interface GA4_retarget_item {
    id: string;
    google_business_vertical: string;
    value: number;
    prod_type: string;
    inst: string;
    portal: number;
}

export interface IjsProduct {
    id: string;
    name: string;
    brand: string;
    category: string;
    variant: string;
    coupon: string;
    position: number;
    list: string; // product List Name for store listing
    itemId: string;
    quantity: number;
}
